/**
 * Bridges the theme's own "Notify me" prompt to Klaviyo's Back in Stock form.
 *
 * Klaviyo injects its own trigger button into #klaviyo-bis-button-container
 * (stacked under the sold-out add-to-cart button). The design instead uses
 * the "Tap 'Notify me' for a restock alert" line in the availability block
 * as the trigger, so the injected button is hidden with CSS and clicks on the
 * theme link are forwarded to it - that keeps Klaviyo's own variant context
 * and popup wiring intact rather than reimplementing them.
 *
 * The popup itself is one generic form for the whole site, and Klaviyo's
 * {product_name} tag doesn't resolve in this form-builder (tested: it
 * renders as literal text). Once the popup mounts, its placeholder heading is
 * replaced with the current product's title, read from the page's own <h1>.
 */
(function () {
  const HEADING_PLACEHOLDER = 'Notify Me';

  function productTitle() {
    // The popup's own heading is an <h1> too, so skip anything inside a
    // Klaviyo form and take the page's product title.
    const heading = Array.prototype.find.call(
      document.querySelectorAll('h1'),
      (h1) => !h1.closest('.klaviyo-form')
    );
    return heading?.textContent.trim() || '';
  }

  function personalize(form) {
    if (form.dataset.klBisPersonalized) {
      scopeRestockLayout(form);
      return;
    }

    // The block renders as <h1>-<h6> or <p> depending on which "Text
    // element" the form editor assigns it, so match on the text, not the tag.
    const heading = Array.prototype.find.call(
      form.querySelectorAll('.klaviyo-form-richtext :is(h1, h2, h3, h4, h5, h6, p)'),
      (el) => el.textContent.trim() === HEADING_PLACEHOLDER
    );
    const title = productTitle();
    if (!heading || !title) return;

    // Scope button overrides to the restock form we have identified.
    form.classList.add('klaviyo-bis-restock-form');

    // Use an actual h5 and remove the nested bold markup supplied by
    // Klaviyo. Keep IDs and other attributes, with responsive typography
    // controlled by the scoped rules in add-to-cart-button.liquid.
    const target = document.createElement('h5');
    for (const attribute of heading.attributes) {
      target.setAttribute(attribute.name, attribute.value);
    }
    for (const property of ['font', 'font-family', 'font-size', 'font-weight', 'font-style', 'line-height']) {
      target.style.removeProperty(property);
    }
    target.classList.add('klaviyo-bis-product-title');
    target.textContent = title;
    form.dataset.klBisPersonalized = 'true';
    heading.replaceWith(target);
    scopeRestockLayout(form);
  }

  function scopeRestockLayout(form) {
    form.classList.add('klaviyo-bis-restock-form');
    // The existing close button is a sibling of the form, not its child.
    // Mark their shared container without moving or replacing that button.
    form.closest('[data-testid="modal-form-container"]')?.classList.add('klaviyo-bis-restock-modal');
    // Klaviyo scales this outer wrapper to fit a fixed-width form on mobile.
    // Give only the restock popup a responsive width instead, so its text,
    // padding and button height retain their intended CSS-pixel sizes.
    form.closest('[data-testid="POPUP"]')?.parentElement?.classList.add('klaviyo-bis-popup-wrapper');
    form.querySelector('.klaviyo-bis-product-title')
      ?.closest('[data-testid="form-row"]')?.classList.add('klaviyo-bis-title-row');
  }

  // Klaviyo's onsite script injects its trigger a beat after the page paints.
  // If the shopper taps "Notify me" before that, remember the intent and open
  // the popup as soon as the button lands (dropped after a few seconds so a
  // stale tap can't pop the form open much later).
  let pendingOpen = 0;
  const PENDING_TTL_MS = 5000;

  function klaviyoButton() {
    return document.querySelector('#klaviyo-bis-button-container button');
  }

  function openPopup() {
    const button = klaviyoButton();
    if (!button) return false;
    button.click();
    return true;
  }

  new MutationObserver(() => {
    document.querySelectorAll('.klaviyo-form').forEach(personalize);

    if (pendingOpen && Date.now() - pendingOpen < PENDING_TTL_MS && openPopup()) {
      pendingOpen = 0;
    }
  }).observe(document.body, { childList: true, subtree: true });

  document.querySelectorAll('.klaviyo-form').forEach(personalize);

  document.addEventListener('click', (event) => {
    const link = event.target.closest('.product-availability__notify-link');
    if (!link) return;

    event.preventDefault();
    pendingOpen = openPopup() ? 0 : Date.now();
  });
})();
